import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { AllCasesContent } from './components/AllCasesContent';
import { SocialMediaContent } from './components/SocialMediaContent';
import { FinanceContent } from './components/FinanceContent';
import { OfficeAutomationContent } from './components/OfficeAutomationContent';
import { EcosystemContent } from './components/EcosystemContent';
import { HealthContent } from './components/HealthContent';
import { DeploymentContent } from './components/DeploymentContent';
import { SocialMediaOverviewContent } from './components/SocialMediaOverviewContent';

interface SubMenuItem {
  id: string;
  label: string;
}

interface MenuItem {
  id: string;
  label: string;
  subItems?: SubMenuItem[];
}

interface MenuGroup {
  title: string;
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    title: '案例分类',
    items: [
      { id: 'all', label: '全部案例' },
      {
        id: 'social',
        label: '社交媒体',
        subItems: [
          { id: 'xiaohongshu', label: '小红书自动发文' },
        ],
      },
      { id: 'finance', label: '金融投资' },
      { id: 'office', label: '办公自动化' },
      { id: 'ecosystem', label: '生态集成' },
      { id: 'health', label: '健康管理' },
      { id: 'development', label: '开发部署' },
    ],
  },
];

const CasesPage = () => {
  const [activeItem, setActiveItem] = useState<string>(() => {
    return localStorage.getItem('cases_active_section') || 'all';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'cases_active_section' && e.newValue) {
        setActiveItem(e.newValue);
      }
    };

    const handleNavChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.storageKey === 'cases_active_section') {
        setActiveItem(detail.key);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('nav-section-change', handleNavChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('nav-section-change', handleNavChange);
    };
  }, []);

  const handleItemClick = (itemId: string) => {
    setActiveItem(itemId);
    localStorage.setItem('cases_active_section', itemId);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const allMenuItems = menuGroups.flatMap((group) =>
    group.items.flatMap((item) =>
      item.subItems ? [item, ...item.subItems] : [item]
    )
  );
  const currentNavItem = allMenuItems.find((item) => item.id === activeItem);

  const isParentActive = (item: MenuItem) => {
    if (activeItem === item.id) return true;
    return item.subItems?.some((sub) => sub.id === activeItem) ?? false;
  };

  const renderContent = () => {
    switch (activeItem) {
      case 'all':
        return <AllCasesContent />;
      case 'social':
        return <SocialMediaOverviewContent />;
      case 'xiaohongshu':
        return <SocialMediaContent />;
      case 'finance':
        return <FinanceContent />;
      case 'office':
        return <OfficeAutomationContent />;
      case 'ecosystem':
        return <EcosystemContent />;
      case 'health':
        return <HealthContent />;
      case 'development':
        return <DeploymentContent />;
      default:
        return <AllCasesContent />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      <Navbar />

      <div className="flex-1 pt-16">
        {/* 移动端顶部选择器 */}
        <div className="md:hidden sticky top-16 z-10 bg-slate-900 border-b border-slate-800">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer"
          >
            <span className="text-white font-medium text-sm">{currentNavItem?.label}</span>
            <i className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-200 ${mobileMenuOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {mobileMenuOpen && (
            <div className="border-t border-slate-800 p-3 bg-slate-900">
              {menuGroups.flatMap((group) =>
                group.items.flatMap((item) => {
                  const rows = [
                    <button
                      key={item.id}
                      onClick={() => handleItemClick(item.id)}
                      className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap mb-1 cursor-pointer ${
                        activeItem === item.id
                          ? 'bg-cyan-500/10 text-cyan-400'
                          : 'text-gray-400 hover:bg-slate-800'
                      }`}
                    >
                      {item.label}
                    </button>,
                  ];
                  if (item.subItems && isParentActive(item)) {
                    item.subItems.forEach((sub) => {
                      rows.push(
                        <button
                          key={sub.id}
                          onClick={() => handleItemClick(sub.id)}
                          className={`w-full text-left pl-8 pr-4 py-2 rounded-lg text-sm whitespace-nowrap mb-1 cursor-pointer ${
                            activeItem === sub.id
                              ? 'text-cyan-400'
                              : 'text-gray-500 hover:text-gray-300 hover:bg-slate-800'
                          }`}
                        >
                          {sub.label}
                        </button>
                      );
                    });
                  }
                  return rows;
                })
              )}
            </div>
          )}
        </div>

        {/* 主内容区 */}
        <div className="flex">
          {/* 左侧边栏 - 桌面端 */}
          <aside className="hidden md:block w-64 border-r border-slate-800 bg-slate-900/50 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="p-6">
              {menuGroups.map((group) => (
                <div key={group.title} className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    {group.title}
                  </h3>
                  <div className="space-y-1">
                    {group.items.map((item) => (
                      <div key={item.id}>
                        <button
                          onClick={() => handleItemClick(item.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                            activeItem === item.id
                              ? 'bg-cyan-500/10 text-cyan-400'
                              : isParentActive(item) && activeItem !== item.id
                              ? 'text-white hover:bg-slate-800'
                              : 'text-gray-400 hover:text-white hover:bg-slate-800'
                          }`}
                        >
                          {item.label}
                        </button>

                        {/* 子菜单：父级激活或子级激活时展开 */}
                        {item.subItems && isParentActive(item) && (
                          <div className="mt-1 ml-3 pl-3 border-l border-slate-700 space-y-1">
                            {item.subItems.map((sub) => (
                              <button
                                key={sub.id}
                                onClick={() => handleItemClick(sub.id)}
                                className={`w-full text-left px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                                  activeItem === sub.id
                                    ? 'text-cyan-400 bg-cyan-500/10'
                                    : 'text-gray-500 hover:text-gray-200 hover:bg-slate-800'
                                }`}
                              >
                                {sub.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </aside>

          {/* 右侧内容区 */}
          <main className="flex-1 min-w-0">
            <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
              {renderContent()}
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CasesPage;